import { ReactNode } from 'react';
import { blockStyles, cx } from '../utils/blockStyles.utils';

interface DashboardBlockProps {
  children: ReactNode;
  className?: string;
}

/**
 * Consistent wrapper component for all dashboard blocks
 * Provides uniform styling and spacing
 */
export const DashboardBlock = ({ children, className = '' }: DashboardBlockProps) => {
  return (
    <div className={cx(blockStyles.container, className)}>
      {children}
    </div>
  );
};

interface DashboardBlockHeaderProps {
  icon?: ReactNode;
  title: string;
  action?: ReactNode;
}

/**
 * Consistent header component for dashboard blocks
 */
export const DashboardBlockHeader = ({ icon, title, action }: DashboardBlockHeaderProps) => {
  return (
    <header className={blockStyles.header.base}>
      <div className="flex items-center gap-2.5">
        {icon && <div className={blockStyles.header.icon}>{icon}</div>}
        <h2 className={blockStyles.header.title}>{title}</h2>
      </div>
      {action && <div className={blockStyles.header.action}>{action}</div>}
    </header>
  );
};

interface DashboardBlockContentProps {
  children: ReactNode;
  className?: string;
  interactive?: boolean;
  padding?: keyof typeof blockStyles.content.padding;
  minHeight?: keyof typeof blockStyles.content.minHeight;
}

/**
 * Consistent content wrapper for dashboard blocks
 */
export const DashboardBlockContent = ({ 
  children, 
  className = '',
  interactive = false,
  padding = 'md',
  minHeight = 'md',
}: DashboardBlockContentProps) => {
  return (
    <div 
      className={cx(
        blockStyles.content.base,
        interactive && blockStyles.content.interactive,
        blockStyles.content.padding[padding],
        blockStyles.content.minHeight[minHeight],
        className
      )}
    >
      {children}
    </div>
  );
};

